import { useState } from 'react';
import { useParams } from 'react-router';
import { failureOf, said } from '@/api/errors';
import {
  useAddParticipant,
  useAddProjectMember,
  useAskForDraft,
  useConveneMeeting,
  useProjectRoom,
  useSayInRoom,
  useDecidePublication,
  useGrantProject,
  useLendTool,
  useProject,
  useRemoveParticipant,
  useRevokeLentTool,
  useSetProjectLevel,
} from '@/api/hooks';
import type { PublicationChannel, WireMeeting, WireProjectDetail, WirePublication, WireRoomMessage } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentLabel } from '@/app/departments';
import { Dialog } from '@/ui/Dialog';
import { LEVEL_SHORT, Meter, Stat, type Level } from '@/ui/meter';
import { Badge, Button, Field, Kicker, Skeleton, TextInput } from '@/ui/primitives';
import { OptionRow, RecordHeader, TokenPanel, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { actorLabel, twinGiven } from '@/today/copy';
import { ComposedLine } from '@/ui/Composed';
import { CHANNEL_LABEL, VOICE_LEVEL, outcomeChip } from './copy';
import { TwinTrailButton } from '@/ui/TwinTrail';
import { ToolLabel, toolName, useTechnical, useToolNames, useWords } from '@/app/technical';
import { Textarea } from '@/ui/fields';
import { Explainer } from '@/ui/context';
import { useWho, type Who } from '@/ui/who';
import { onItsPage } from '@/requests/explain';
import { explainLentTool, explainProject, explainPublication, explainRoomAction, explainTwinGrant } from './explain';
import { localIsoDate } from '@/today/dates';

/**
 * One project (ADR-0056, ADR-0059): who works on it, which twins it lets
 * draft and until when, its level and the drafts that earn it, and the
 * drafts themselves — words shown to the person whose voice they are, and
 * to everyone once posted. Only that person decides a draft, and nothing
 * here posts: "posted" is them saying they posted it.
 */
export function ProjectPage({ recordId }: { recordId?: string } = {}) {
  const params = useParams();
  const id = recordId ?? params.id ?? '';
  const me = useMeOrThrow();
  const departmentLabel = useDepartmentLabel();
  const q = useProject(id);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={10} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const { project, member, myTwin } = q.data;
  const owner = project.memberList.some((m) => m.humanId === me.humanId && m.role === 'owner');
  const mine = project.publications.filter((p) => p.outcome === 'pending' && p.forHuman === me.humanId);
  const othersWaiting = project.publications.filter((p) => p.outcome === 'pending' && p.forHuman !== me.humanId);
  const decided = project.publications.filter((p) => p.outcome !== 'pending');
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4" data-testid="project">
      <RecordHeader
        kicker="Project"
        title={project.title}
        subtitle={project.description || null}
        meta={`${project.department ? `Led by ${departmentLabel(project.department)}` : 'Across departments'} · owned by ${project.owner?.displayName ?? 'nobody'} · opened ${when(project.createdAt)} · ${project.members} ${project.members === 1 ? 'participant' : 'participants'}`}
        chips={[
          { label: `VOICE · ${LEVEL_SHORT[project.level].toUpperCase()}`, tone: project.level === 'open_loop' ? 'neutral' : 'accent' },
          ...(project.department === null ? [{ label: 'ACROSS DEPARTMENTS', tone: 'accent' as const }] : []),
          ...(project.state !== 'active' ? [{ label: project.state.toUpperCase(), tone: 'neutral' as const }] : []),
        ]}
        actions={<TwinTrailButton context={{ projectId: project.id }} where={project.title} />}
      />
      <Explainer
        label="About this project"
        startOpen
        context={onItsPage(explainProject(project, who, { humanId: me.humanId, member, myTwin }), `/projects/${project.id}`)}
      />
      <Room project={project} who={who} />
      <Ladder project={project} superadmin={me.isSuperadmin} />
      <Grants project={project} superadmin={me.isSuperadmin} />
      <LentTools project={project} superadmin={me.isSuperadmin} who={who} />
      {member && myTwin && <AskForDraft project={project} twin={myTwin} />}

      {mine.length > 0 && (
        <section className="flex flex-col gap-3" aria-label="Your drafts" data-testid="my-drafts">
          <Kicker>In your voice, waiting for you</Kicker>
          {mine.map((p) => (
            <DecideDraft key={p.id} publication={p} project={project} who={who} humanId={me.humanId} />
          ))}
        </section>
      )}
      {othersWaiting.length > 0 && (
        <section className="flex flex-col gap-2" aria-label="Waiting for others">
          <Kicker>Waiting for others</Kicker>
          {othersWaiting.map((p) => (
            <div key={p.id} className="flex flex-col gap-1">
              <p className="m-0 text-dense text-secondary">
                {CHANNEL_LABEL[p.channel]} in {p.forName}’s voice, drafted {when(p.createdAt)} — theirs to read and decide.
              </p>
              <Explainer context={explainPublication(p, project, who, { humanId: me.humanId })} />
            </div>
          ))}
        </section>
      )}
      <section className="flex flex-col gap-2" aria-label="What happened to the drafts" data-testid="decided">
        <Kicker>What happened to the drafts</Kicker>
        {decided.length === 0 && <p className="m-0 text-dense text-secondary">Nothing decided yet.</p>}
        {decided.map((p) => (
          <article key={p.id} className="flex flex-col gap-1 rounded-brand border border-line p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-body font-semibold">{p.title ?? CHANNEL_LABEL[p.channel]}</span>
              <Badge tone={outcomeChip(p.outcome).tone}>{outcomeChip(p.outcome).label}</Badge>
              {p.outcome === 'posted_edited' && p.editDistance !== null && (
                <span className="text-dense text-meta">{p.editDistance} characters changed</span>
              )}
            </div>
            <span className="text-dense text-meta">
              {CHANNEL_LABEL[p.channel]} · {p.forName}’s voice · drafted by {actorLabel(p.draftedBy, p.forName)} · decided {when(p.decidedAt)}
            </span>
            {(p.final ?? p.body) && <p className="m-0 whitespace-pre-wrap text-dense">{p.final ?? p.body}</p>}
            <Explainer context={explainPublication(p, project, who, { humanId: me.humanId })} />
          </article>
        ))}
      </section>

      <Members project={project} owner={owner} superadmin={me.isSuperadmin} />
    </div>
  );
}

const MEETING_STATE: Record<WireMeeting['state'], { label: string; tone: 'accent' | 'warning' | 'neutral' }> = {
  running: { label: 'IN THE MEETING', tone: 'warning' },
  minuting: { label: 'WRITING THE MINUTES', tone: 'warning' },
  minuted: { label: 'MINUTED', tone: 'accent' },
  abandoned: { label: 'ABANDONED', tone: 'neutral' },
};

const KIND_WORD: Record<WireRoomMessage['kind'], string> = {
  said: '',
  minutes: 'Minutes',
  decision: 'Decided',
  action: 'Action',
};

/**
 * The project's room (Amendment 0009, ADR-0125): its people and its agents,
 * from every department on it, talking the work through. A person says
 * something; a meeting gives each agent its turn from its own department;
 * the minutes say what was decided and who does what, and each action went
 * to that agent's department as a task or a request. Nothing runs from here.
 */
function Room({ project, who }: { project: WireProjectDetail; who: Who }) {
  const q = useProjectRoom(project.id);
  const say = useSayInRoom();
  const toast = useToast();
  const [text, setText] = useState('');
  const [convening, setConvening] = useState(false);
  if (q.isPending) return <Skeleton rows={3} />;
  if (q.isError) return null;
  const { messages, meetings, canSay, canConvene } = q.data;
  const live = meetings.find((m) => m.state === 'running' || m.state === 'minuting');
  const closed = project.state !== 'active';
  return (
    <section className="flex flex-col gap-3 rounded-brand border border-line p-5" aria-label="The room" data-testid="room">
      <div className="flex flex-wrap items-baseline gap-2">
        <Kicker>The room</Kicker>
        <span className="text-dense text-secondary">Everyone on the project, from every department on it, people and agents.</span>
        {canConvene && !closed && (
          <Button size="row" variant="secondary" className="ml-auto" disabled={Boolean(live)} onClick={() => setConvening(true)}>
            Hold a meeting
          </Button>
        )}
      </div>
      {live && (
        <p className="m-0 flex flex-wrap items-center gap-2 text-dense" data-testid="meeting-live">
          <Badge tone={MEETING_STATE[live.state].tone}>{MEETING_STATE[live.state].label}</Badge>
          <span>
            “{live.agenda}” — {live.state === 'running'
              ? `${live.speakers[live.turn % live.speakers.length]?.name ?? 'the next agent'} is speaking (turn ${live.turn + 1} of ${live.speakers.length * live.rounds})`
              : `${live.minutedByDepartment} is writing the minutes`}
          </span>
        </p>
      )}
      {messages.length === 0 ? (
        <p className="m-0 text-dense text-secondary">Nothing has been said here yet.</p>
      ) : (
        <ol className="m-0 flex list-none flex-col gap-2 p-0" data-testid="room-messages">
          {messages.map((m) => (
            <li key={m.id} className={`flex flex-col gap-1 rounded-brand p-3 ${m.kind === 'said' ? 'bg-page border border-line' : 'bg-raised'}`} data-testid="room-message" data-kind={m.kind}>
              <span className="flex flex-wrap items-baseline gap-2 text-dense text-meta">
                <span className="font-semibold text-primary">{m.author.name}</span>
                {m.author.kind === 'agent' && m.author.department && <span>{m.author.department}</span>}
                {KIND_WORD[m.kind] && <Badge plain>{KIND_WORD[m.kind]}{m.actionFor ? ` · ${m.actionFor.name}` : ''}</Badge>}
                {m.kind === 'action' && (m.requestId ? <span>asked of {m.actionFor?.department} as a request</span> : m.taskId ? <span>a task for them</span> : <span>filed nowhere</span>)}
                <span className="ml-auto">{when(m.at)}</span>
              </span>
              {m.body === null ? (
                <span className="text-dense text-secondary">Withheld: the scan blocked it. It was said, and it is not shown or read into anyone’s prompt.</span>
              ) : (
                <p className="m-0 whitespace-pre-wrap text-dense">{m.body}</p>
              )}
              {m.kind === 'action' && <Explainer context={explainRoomAction(m, project, who)} />}
            </li>
          ))}
        </ol>
      )}
      {canSay && !closed && (
        <div className="flex flex-col gap-2">
          <Textarea
            aria-label="Say something to everyone on the project"
            placeholder="Say something to everyone on the project"
            value={text}
            maxLength={4000}
            onChange={(e) => setText(e.target.value)}
            className="min-h-20"
          />
          <Button
            size="row"
            variant="primary"
            className="w-fit"
            disabled={text.trim() === '' || say.isPending}
            onClick={() =>
              say.mutate(
                { projectId: project.id, body: text },
                { onSuccess: () => { setText(''); toast({ text: 'Said. Everyone on the project sees it.' }); } },
              )
            }
          >
            Say it
          </Button>
          {say.isError && <Refusal failure={failureOf(say.error)} compact />}
        </div>
      )}
      {convening && <Convene project={project} onClose={() => setConvening(false)} />}
    </section>
  );
}

function Convene({ project, onClose }: { project: WireProjectDetail; onClose: () => void }) {
  const convene = useConveneMeeting();
  const toast = useToast();
  const [agenda, setAgenda] = useState('');
  const [rounds, setRounds] = useState<'1' | '2' | '3'>('2');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Amendment 0009 · a meeting"
      title={`Hold a meeting on ${project.title}`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Convene it',
        disabled: agenda.trim() === '' || convene.isPending,
        onClick: () =>
          convene.mutate(
            { projectId: project.id, agenda, rounds: Number(rounds) },
            {
              onSuccess: (r) => {
                toast({ text: `Convened: ${r.speakers} ${r.speakers === 1 ? 'agent speaks' : 'agents speak'} in turn, then the minutes.` });
                onClose();
              },
            },
          ),
      }}
    >
      <p className="m-0 text-body">
        Each agent on the project speaks in turn, from its own department, having heard everyone before it and what people said here. Then one writes the minutes: what was decided, and who does what. An action for another department is asked of it as a request; nothing runs from the meeting itself.
      </p>
      <Field label="Agenda">
        <TextInput value={agenda} onChange={(e) => setAgenda(e.target.value)} aria-label="Agenda" placeholder="What should the meeting settle?" />
      </Field>
      <OptionRow<'1' | '2' | '3'>
        name="Rounds"
        value={rounds}
        onChoose={setRounds}
        options={[
          { value: '1', label: 'One round' },
          { value: '2', label: 'Two rounds' },
          { value: '3', label: 'Three rounds' },
        ]}
      />
      {convene.isError && <Refusal failure={failureOf(convene.error)} compact />}
    </Dialog>
  );
}

/**
 * What the project lends its participants (Amendment 0008, ADR-0123 §1): one
 * tool at a time, under a token bound to this project and that tool, and
 * never past any participant's own department floor. Taking one back spends
 * nothing.
 */
function LentTools({ project, superadmin, who }: { project: WireProjectDetail; superadmin: boolean; who: Who }) {
  const words = useWords();
  const revoke = useRevokeLentTool();
  const toast = useToast();
  const [lending, setLending] = useState(false);
  return (
    <section className="flex flex-col gap-2" aria-label="What this project lends" data-testid="lent-tools">
      <Kicker>{words('What this project lends (Amendment 0008)')}</Kicker>
      {project.toolGrants.length === 0 && (
        <p className="m-0 text-dense text-secondary">Nothing. Everyone here works with their own department’s tools.</p>
      )}
      {project.toolGrants.map((g) => (
        <div key={g.id} className="flex flex-col gap-1">
        <p className="m-0 flex flex-wrap items-center gap-2 text-dense" data-testid="lent-tool">
          <span className="text-dense font-semibold"><ToolLabel id={g.toolId} /></span>
          <span className="text-meta">
            lent by {actorLabel(g.grantedBy)}
            {g.validTo ? ` until ${new Date(g.validTo).toLocaleDateString('en-GB')}` : ' for the life of the project'}
          </span>
          {superadmin && (
            <Button
              size="row"
              variant="quiet"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate({ grantId: g.id }, { onSuccess: () => toast({ text: `${toolName(g.toolId)} is no longer lent by ${project.title}.` }) })}
            >
              Take it back
            </Button>
          )}
        </p>
        <Explainer context={explainLentTool(g, project, who)} />
        </div>
      ))}
      <p className="m-0 text-dense text-meta">
        A department’s never: list always wins: a tool it forbids is lent to nobody from it, whatever this project says. Taking one back spends no token.
      </p>
      {superadmin && project.state === 'active' && (
        <div>
          <Button size="row" onClick={() => setLending(true)}>Lend a tool</Button>
        </div>
      )}
      {revoke.isError && <Refusal failure={failureOf(revoke.error)} compact />}
      {lending && <LendTool project={project} onClose={() => setLending(false)} />}
    </section>
  );
}

function LendTool({ project, onClose }: { project: WireProjectDetail; onClose: () => void }) {
  const lend = useLendTool();
  const toast = useToast();
  const [toolId, setToolId] = useState('');
  const [until, setUntil] = useState('');
  const [token, setToken] = useState('');
  const id = toolId.trim();
  const wellFormed = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/.test(id);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Amendment 0008 · the floor"
      title={`Lend a tool to ${project.title}`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Mint the token and lend it',
        disabled: token === '' || lend.isPending || !wellFormed || (until !== '' && new Date(until) <= new Date()),
        onClick: () =>
          lend.mutate(
            { projectId: project.id, toolId: id, ...(until ? { validTo: new Date(`${until}T23:59:59Z`).toISOString() } : {}), supervisionToken: token },
            {
              onSuccess: () => {
                toast({ text: `${id} is lent to everyone on ${project.title}${until ? ` until ${new Date(until).toLocaleDateString('en-GB')}` : ''}.` });
                onClose();
              },
            },
          ),
      }}
    >
      <p className="m-0 text-body">
        Every participant, from any department, may use it inside this project. It is refused if any participant’s own department has put what it needs on its never: list — that list is never crossed — and it ends with the project.
      </p>
      <Field label="Tool" hint="Its id in the ledger, such as outbound.notify_owner.">
        <TextInput
          value={toolId}
          onChange={(e) => {
            setToolId(e.target.value);
            // The token is bound to the tool as typed when it was minted.
            setToken('');
          }}
          aria-label="Tool"
          placeholder="outbound.notify_owner"
        />
      </Field>
      <Field label="Ends" hint="Leave empty to end with the project.">
        <input type="date" className="min-h-tap rounded-brand border border-control bg-raised px-3 text-body" value={until} onChange={(e) => setUntil(e.target.value)} aria-label="Ends" />
      </Field>
      <TokenPanel operation="project.tool_grant" target={`project:${project.id}:tool:${id}`} token={token} onToken={setToken} canMint />
      {lend.isError && <Refusal failure={failureOf(lend.error)} compact />}
    </Dialog>
  );
}

/**
 * Someone from any department put on the project (ADR-0123 §5): a person by
 * email or a department agent by address, under a token bound to this
 * project and that address, because joining is what puts them within reach
 * of everything the project lends. A twin is lent through its person.
 */
function AddParticipant({ project, onClose }: { project: WireProjectDetail; onClose: () => void }) {
  const add = useAddParticipant();
  const toast = useToast();
  const [who, setWho] = useState('');
  const [role, setRole] = useState<'member' | 'observer'>('member');
  const [token, setToken] = useState('');
  const address = who.trim().toLowerCase();
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="ADR-0123 · a named participant"
      title={`Add someone to ${project.title}`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Mint the token and add them',
        disabled: token === '' || add.isPending || address.length < 3,
        onClick: () =>
          add.mutate(
            { projectId: project.id, who: address, role, supervisionToken: token },
            {
              onSuccess: (r) => {
                toast({ text: `${r.who} is on ${project.title} as ${r.role}${r.kind === 'agent' ? ', and reaches what it lends' : ''}.` });
                onClose();
              },
            },
          ),
      }}
    >
      <p className="m-0 text-body">
        A person by email, or a department agent by address, from any department. A twin is not added: it is lent through its person. Whoever joins is within reach of everything this project lends, so the token is bound to this project and this address.
      </p>
      <Field label="Who">
        <TextInput
          value={who}
          onChange={(e) => {
            setWho(e.target.value);
            setToken('');
          }}
          aria-label="Who"
          placeholder="raquel@norumfoods.com or otis.agent@norumfoods.com"
        />
      </Field>
      <OptionRow<'member' | 'observer'>
        name="Role"
        value={role}
        onChoose={setRole}
        options={[
          { value: 'member', label: 'Member' },
          { value: 'observer', label: 'Observer' },
        ]}
      />
      <TokenPanel operation="project.member" target={`project:${project.id}:member:${address}`} token={token} onToken={setToken} canMint />
      {add.isError && <Refusal failure={failureOf(add.error)} compact />}
    </Dialog>
  );
}

function Ladder({ project, superadmin }: { project: WireProjectDetail; superadmin: boolean }) {
  const [moving, setMoving] = useState(false);
  const e = project.evidence;
  const next = project.next;
  const pct = (n: number) => `${Math.round(n * 100)}%`;
  const sentence = !next
    ? 'At the highest level.'
    : e.decided >= next.decided && e.postedShare >= next.minPostedShare && e.unchangedShare >= next.minUnchangedShare
      ? `The drafts earn ${LEVEL_SHORT[next.level as Level].toLowerCase()}. A superadmin still has to grant it.`
      : `${LEVEL_SHORT[next.level as Level]} needs ${next.decided} drafts decided, ${pct(next.minPostedShare)} posted and ${pct(next.minUnchangedShare)} posted as drafted; so far ${e.decided} decided, ${pct(e.postedShare)} posted, ${pct(e.unchangedShare)} as drafted.`;
  return (
    <section className="flex flex-col gap-3" aria-label="The ladder" data-testid="ladder">
      <Kicker>Earned on the drafts (§3, ADR-0059)</Kicker>
      <Meter level={project.level} label="How much the twin may publish unread" descriptions={VOICE_LEVEL} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat value={String(e.postedUnchanged)} label="Posted as drafted" tone="accent" />
        <Stat value={String(e.postedEdited)} label="Posted with edits" />
        <Stat value={String(e.discarded)} label="Discarded" />
      </div>
      <p className="m-0 text-dense text-secondary" data-testid="ladder-sentence">{sentence}</p>
      <p className="m-0 text-dense text-meta">
        No tool to post with is in the library, so every piece is posted by its person, whatever the level says.
      </p>
      {superadmin && (
        <div>
          <Button size="row" onClick={() => setMoving(true)}>Move the level</Button>
        </div>
      )}
      {moving && <MoveLevel project={project} onClose={() => setMoving(false)} />}
    </section>
  );
}

function MoveLevel({ project, onClose }: { project: WireProjectDetail; onClose: () => void }) {
  const set = useSetProjectLevel();
  const toast = useToast();
  const [level, setLevel] = useState<Level>(project.level);
  const [evidence, setEvidence] = useState('');
  const [token, setToken] = useState('');
  const order: Level[] = ['open_loop', 'mixed', 'closed_loop'];
  const raising = order.indexOf(level) > order.indexOf(project.level);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Earned, not granted"
      title={`Move ${project.title}’s level`}
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: raising ? 'Mint the token and raise it' : 'Lower it',
        disabled: level === project.level || set.isPending || (raising && (token === '' || evidence.trim().length < 12)),
        onClick: () =>
          set.mutate(
            { projectId: project.id, level, ...(raising ? { evidence: evidence.trim(), supervisionToken: token } : {}) },
            { onSuccess: () => { toast({ text: `${project.title} is at ${LEVEL_SHORT[level].toLowerCase()}.` }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">A project’s level is its own, earned on its drafts. Raising it needs the evidence and a token bound to this project; lowering needs neither.</p>
      <Meter level={level} onSet={setLevel} label="Level" descriptions={VOICE_LEVEL} />
      {raising && (
        <>
          <Field label="Your evidence, in your words">
            <Textarea className="min-h-[88px]" value={evidence} onChange={(e) => setEvidence(e.target.value)} aria-label="Evidence" />
          </Field>
          <TokenPanel operation="autonomy.change" target={`autonomy:project:${project.id}:publishing`} token={token} onToken={setToken} canMint />
        </>
      )}
      {set.isError && <Refusal failure={failureOf(set.error)} compact />}
    </Dialog>
  );
}

function Grants({ project, superadmin }: { project: WireProjectDetail; superadmin: boolean }) {
  const names = useToolNames();
  const [granting, setGranting] = useState(false);
  const ungranted = project.memberList.filter((m) => m.twin && !project.grants.some((g) => g.agentId === m.twin!.id));
  return (
    <section className="flex flex-col gap-2" aria-label="Who may draft here" data-testid="grants">
      <Kicker>Who may draft here (§15)</Kicker>
      {project.grants.length === 0 && (
        <p className="m-0 text-dense text-secondary">No twin holds this project. Until one does, nothing is drafted here.</p>
      )}
      {project.grants.map((g) => (
        <div key={g.assignmentId} className="flex flex-col gap-1">
          <p className="m-0 text-dense">
            <span className="font-semibold">{actorLabel(g.agentHandle, g.principalName ?? undefined)}</span> — {g.title}
            {g.validTo ? `, until ${new Date(g.validTo).toLocaleDateString('en-GB')}` : ''}
            {g.tools.length > 0 ? ` · ${names(g.tools)}` : ''}
          </p>
          <Explainer context={explainTwinGrant(g, project)} />
        </div>
      ))}
      {superadmin && ungranted.length > 0 && (
        <div>
          <Button size="row" onClick={() => setGranting(true)}>Give a twin this project</Button>
        </div>
      )}
      {granting && <GrantProject project={project} candidates={ungranted} onClose={() => setGranting(false)} />}
    </section>
  );
}

function GrantProject({
  project,
  candidates,
  onClose,
}: {
  project: WireProjectDetail;
  candidates: WireProjectDetail['memberList'];
  onClose: () => void;
}) {
  const grant = useGrantProject();
  const toast = useToast();
  const [who, setWho] = useState(candidates[0]!.twin!.id);
  const [tools, setTools] = useState('platform.vertex_gemini_flash_eu');
  const [until, setUntil] = useState(() => localIsoDate(new Date(Date.now() + 30 * 86_400_000)));
  const [token, setToken] = useState('');
  const twin = candidates.find((c) => c.twin?.id === who)!.twin!;
  const list = tools.split(/[\s,]+/).filter(Boolean);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="§15 · capability follows the work"
      title="Give a twin this project"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Mint the token and grant it',
        disabled: token === '' || grant.isPending || new Date(until) <= new Date(),
        onClick: () =>
          grant.mutate(
            { agentId: who, projectId: project.id, title: `Draft for ${project.title}`, validTo: new Date(`${until}T23:59:59Z`).toISOString(), tools: list, supervisionToken: token },
            { onSuccess: () => { toast({ text: `${actorLabel(twin.handle, candidates.find((c) => c.twin?.id === who)?.displayName)} may draft in ${project.title} until ${new Date(until).toLocaleDateString('en-GB')}.` }); onClose(); } },
          ),
      }}
    >
      <p className="m-0 text-body">The grant holds inside this project and nowhere else, and it ends. The token is bound to this twin and this project.</p>
      <OptionRow<string>
        name="Twin"
        value={who}
        onChoose={setWho}
        options={candidates.map((c) => ({ value: c.twin!.id, label: actorLabel(c.twin!.handle, c.displayName), hint: `works on ${project.title}` }))}
      />
      <Field label="Tools" hint="What drafting may use, from the library.">
        <TextInput value={tools} onChange={(e) => setTools(e.target.value)} aria-label="Tools" />
      </Field>
      <Field label="Ends">
        <input type="date" className="min-h-tap rounded-brand border border-control bg-raised px-3 text-body" value={until} onChange={(e) => setUntil(e.target.value)} aria-label="Ends" />
      </Field>
      <TokenPanel operation="iam.grant_scope" target={`agent:${twin.handle}:project:${project.id}`} token={token} onToken={setToken} canMint />
      {grant.isError && <Refusal failure={failureOf(grant.error)} compact />}
    </Dialog>
  );
}

function AskForDraft({ project, twin }: { project: WireProjectDetail; twin: { handle: string; granted: boolean } }) {
  const ask = useAskForDraft();
  const toast = useToast();
  const [channel, setChannel] = useState<PublicationChannel>('blog');
  if (!twin.granted) {
    return (
      <p className="m-0 border-l-2 border-line pl-3 text-dense text-secondary" data-testid="ask-draft">
        {twinGiven(twin.handle)}, your twin, holds no assignment here, so it drafts nothing for this project. A superadmin can give it one.
      </p>
    );
  }
  return (
    <section className="flex flex-col gap-2 rounded-brand border border-strong p-4" aria-label="Ask for a draft" data-testid="ask-draft">
      <Kicker>Ask {twinGiven(twin.handle)}, your twin, for the next piece</Kicker>
      <p className="m-0 text-dense text-secondary">It writes from what this project is for and what you have already posted here. It waits for you; it never posts.</p>
      <OptionRow<PublicationChannel>
        name="Where it would go"
        value={channel}
        onChoose={setChannel}
        options={(Object.keys(CHANNEL_LABEL) as PublicationChannel[]).map((c) => ({ value: c, label: CHANNEL_LABEL[c] }))}
      />
      <div>
        <Button icon="ask"
          variant="primary"
          disabled={ask.isPending}
          onClick={() =>
            ask.mutate(
              { projectId: project.id, channel },
              {
                onSuccess: (r) => toast({ text: `${twinGiven(r.twin)}, your twin, is drafting. It lands here, for you, when the heartbeat has run it.` }),
                onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
              },
            )
          }
        >Ask for a draft</Button>
      </div>
    </section>
  );
}

function DecideDraft({ publication, project, who, humanId }: { publication: WirePublication; project: WireProjectDetail; who: Who; humanId: string }) {
  const decide = useDecidePublication();
  const toast = useToast();
  const [text, setText] = useState(publication.body ?? '');
  const edited = text.trim() !== (publication.body ?? '').trim();
  const done = (label: string) => () => toast({ text: label });
  return (
    <article className="flex flex-col gap-2 rounded-brand border border-accent p-4" data-testid="draft">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body font-semibold">{publication.title ?? CHANNEL_LABEL[publication.channel]}</span>
        <Badge tone="warning">{CHANNEL_LABEL[publication.channel].toUpperCase()}</Badge>
        <span className="text-dense text-meta">drafted by {actorLabel(publication.draftedBy, publication.forName)} · {when(publication.createdAt)}</span>
        <ComposedLine composed={publication.composed} />
      </div>
      <Textarea
        aria-label="The draft"
        className="min-h-[140px] w-full"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          size="row"
          variant="primary"
          disabled={decide.isPending || edited}
          onClick={() => decide.mutate({ publicationId: publication.id, outcome: 'posted_unchanged' }, { onSuccess: done('Recorded as posted as drafted. That counts toward the project’s level.') })}
        >
          I posted it as drafted
        </Button>
        <Button
          size="row"
          disabled={decide.isPending || !edited}
          onClick={() =>
            decide.mutate(
              { publicationId: publication.id, outcome: 'posted_edited', final: text },
              { onSuccess: (r) => toast({ text: `Recorded with your edits — ${r.editDistance ?? 0} characters changed.` }) },
            )
          }
        >
          I posted it with my edits
        </Button>
        <Button
          size="row"
          disabled={decide.isPending}
          onClick={() => decide.mutate({ publicationId: publication.id, outcome: 'discarded' }, { onSuccess: done('Discarded. It stays yours and is counted as one that did not go out.') })}
        >
          Discard it
        </Button>
      </div>
      {decide.isError && <Refusal failure={failureOf(decide.error)} compact />}
      <Explainer context={explainPublication(publication, project, who, { humanId })} />
    </article>
  );
}

function Members({ project, owner, superadmin }: { project: WireProjectDetail; owner: boolean; superadmin: boolean }) {
  const tech = useTechnical();
  const departmentLabel = useDepartmentLabel();
  const add = useAddProjectMember();
  const remove = useRemoveParticipant();
  const toast = useToast();
  const [who, setWho] = useState('');
  const [adding, setAdding] = useState(false);
  const takeOff = (address: string) => (
    <Button
      size="row"
      variant="quiet"
      disabled={remove.isPending}
      onClick={() => remove.mutate({ projectId: project.id, who: address }, { onSuccess: () => toast({ text: `${address} is off ${project.title}.` }) })}
    >
      Take off
    </Button>
  );
  return (
    <section className="flex flex-col gap-2" aria-label="People" data-testid="members">
      <Kicker>People and agents</Kicker>
      {project.memberList.map((m) => (
        <p key={m.humanId} className="m-0 flex flex-wrap items-center gap-2 text-dense">
          <span>
            {m.displayName} · {m.role}
            {m.twin ? ` · with a twin${tech ? ` (${m.twin.handle})` : ''}` : ''}
          </span>
          {superadmin && m.role !== 'owner' && takeOff(m.email)}
        </p>
      ))}
      {project.agentMembers.map((a) => (
        <p key={a.agentId} className="m-0 flex flex-wrap items-center gap-2 text-dense" data-testid="agent-member">
          <span>
            {a.givenName} Agent · {a.role}
            {tech ? ` · ${a.handle}` : ''}
            {a.department ? ` · ${departmentLabel(a.department)}` : ''}
          </span>
          {superadmin && takeOff(a.handle)}
        </p>
      ))}
      {superadmin && project.state === 'active' && (
        <div>
          <Button icon="add" size="row" onClick={() => setAdding(true)}>Add someone from any department</Button>
        </div>
      )}
      {remove.isError && <Refusal failure={failureOf(remove.error)} compact />}
      {adding && <AddParticipant project={project} onClose={() => setAdding(false)} />}
      {owner && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Add someone">
            <TextInput value={who} onChange={(e) => setWho(e.target.value)} placeholder="raquel@norumfoods.com" aria-label="Add someone" />
          </Field>
          <Button icon="add"
            size="row"
            disabled={add.isPending || !who.includes('@')}
            onClick={() => add.mutate({ projectId: project.id, who: who.trim() }, { onSuccess: () => { toast({ text: `${who.trim()} is on the project.` }); setWho(''); } })}
          >Add</Button>
        </div>
      )}
      {add.isError && <Refusal failure={failureOf(add.error)} compact />}
    </section>
  );
}
